import { useCallback, useRef, useState } from 'react';
import { MAX_MULTI_ADD_PRODUCTS } from './multiEdit';
import { copyDraftRow, DRAFT_ID_PREFIX } from './useInlineRow';
import { TableRow } from './types';

export interface DraftRowsApi {
  /** Open drafts, in the order the grid shows them. */
  draftRows: TableRow[];
  /** Replace every draft with one draft seeded from `seed`; returns its id. */
  startDrafts: (seed: TableRow) => string;
  /** Add a draft seeded from `seed` at the end; false when the cap is reached. */
  appendDraft: (seed: TableRow) => boolean;
  /** Copy a draft into a new draft right below it; false when the cap is reached. */
  addDraftBelow: (rowId: string) => boolean;
  removeDraft: (rowId: string) => void;
  /** Apply an edit to one draft; a null result leaves it unchanged. */
  updateDraft: (rowId: string, updater: (row: TableRow) => TableRow | null) => void;
  /** Close the drafts whose ids are given — the ones a save created. */
  dropDrafts: (rowIds: Set<string>) => void;
  clearDrafts: () => void;
}

/**
 * The table view's unsaved product drafts (BM-9594 multi-add): an ordered list, so a copied row can
 * sit directly below its source, capped at MAX_MULTI_ADD_PRODUCTS. Every change replaces only the
 * draft it touches, so the grid keeps the other rows' identity and does not redraw them.
 * @returns the drafts and the operations on them
 */
export const useDraftRows = (): DraftRowsApi => {
  const [draftRows, setDraftRows] = useState<TableRow[]>([]);
  const draftRowsRef = useRef<TableRow[]>(draftRows);
  const sequenceRef = useRef(0);
  draftRowsRef.current = draftRows;

  const nextDraftId = useCallback((): string => {
    sequenceRef.current += 1;
    return `${DRAFT_ID_PREFIX}${sequenceRef.current}`;
  }, []);

  const startDrafts = useCallback(
    (seed: TableRow): string => {
      const draftId = nextDraftId();
      setDraftRows([{ ...seed, _id: draftId, __isDraft: true }]);
      return draftId;
    },
    [nextDraftId],
  );

  const appendDraft = useCallback(
    (seed: TableRow): boolean => {
      if (draftRowsRef.current.length >= MAX_MULTI_ADD_PRODUCTS) return false;
      const draft: TableRow = { ...seed, _id: nextDraftId(), __isDraft: true };
      setDraftRows((previous) => [...previous, draft]);
      return true;
    },
    [nextDraftId],
  );

  const addDraftBelow = useCallback(
    (rowId: string): boolean => {
      if (draftRowsRef.current.length >= MAX_MULTI_ADD_PRODUCTS) return false;
      const copyId = nextDraftId();
      setDraftRows((previous) => {
        const sourceIndex = previous.findIndex((row) => row._id === rowId);
        const source = previous[sourceIndex];
        if (!source) return previous;
        const copy = copyDraftRow(source, copyId);
        return [...previous.slice(0, sourceIndex + 1), copy, ...previous.slice(sourceIndex + 1)];
      });
      return true;
    },
    [nextDraftId],
  );

  const removeDraft = useCallback((rowId: string) => {
    setDraftRows((previous) => previous.filter((row) => row._id !== rowId));
  }, []);

  const updateDraft = useCallback((rowId: string, updater: (row: TableRow) => TableRow | null) => {
    setDraftRows((previous) => {
      const current = previous.find((row) => row._id === rowId);
      if (!current) return previous;
      const updated = updater(current);
      if (!updated || updated === current) return previous;
      return previous.map((row) => (row === current ? updated : row));
    });
  }, []);

  const dropDrafts = useCallback((rowIds: Set<string>) => {
    setDraftRows((previous) => (rowIds.size ? previous.filter((row) => !rowIds.has(String(row._id))) : previous));
  }, []);

  const clearDrafts = useCallback(() => {
    setDraftRows((previous) => (previous.length ? [] : previous));
  }, []);

  return { draftRows, startDrafts, appendDraft, addDraftBelow, removeDraft, updateDraft, dropDrafts, clearDrafts };
};
